import axios, { AxiosError, AxiosInstance } from "axios";

import i18n from "../i18n/config";
import {
  AnswerBluffRoomControlResponse,
  AnswerBluffRoomStartResponse,
  ImposterOnlinePublicJoinResponse,
  ImposterOnlinePublicLobbiesResponse,
  ImposterOnlineRoomCreateResponse,
  AuthResponse,
  Category,
  CreateGameSessionRequest,
  ErrorResponse,
  GameMode,
  GameSession,
  GameSessionModeCategoryContent,
  LoginRequest,
  ModeCategoryRow,
  ModeDareCardRow,
  ModeGuessContentRow,
  ModeQuickQuestionRow,
  ModeRegistryEntry,
  ModeTopicRow,
  ModeWordContentRow,
  PaymentMethod,
  PurchaseOption,
  SignUpRequest,
  User,
  UserCreditsResponse,
} from "../types/api";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

// Helper function to normalize array responses from API
function normalizeArrayResponse<T>(data: any): T[] {
  // Handle direct array response
  if (Array.isArray(data)) {
    return data;
  }
  // Handle paginated response
  if (
    data &&
    typeof data === "object" &&
    "results" in data &&
    Array.isArray(data.results)
  ) {
    return data.results;
  }
  // Handle wrapped response
  if (
    data &&
    typeof data === "object" &&
    "data" in data &&
    Array.isArray(data.data)
  ) {
    return data.data;
  }
  // Fallback to empty array
  return [];
}

class ApiClient {
  private client: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (reason?: any) => void;
  }> = [];

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Add language header to all requests
    this.client.interceptors.request.use((config) => {
      const language = i18n.language || "ar";
      config.headers["Accept-Language"] = language;
      return config;
    });

    // Add auth token to requests
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem("access_token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle token refresh on 401
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ErrorResponse>) => {
        const originalRequest = error.config as any;

        // If error is 401 and we haven't tried to refresh yet
        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            // If already refreshing, queue this request
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return this.client(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          const refreshToken = localStorage.getItem("refresh_token");
          if (!refreshToken) {
            this.processQueue(null, new Error("No refresh token"));
            this.logout();
            window.location.href = "/login";
            return Promise.reject(error);
          }

          try {
            const response = await axios.post(
              `${API_BASE_URL}/api/users/token/refresh/`,
              { refresh: refreshToken },
            );

            const { access, refresh: newRefreshToken } = response.data;
            localStorage.setItem("access_token", access);
            if (newRefreshToken) {
              localStorage.setItem("refresh_token", newRefreshToken);
            }

            originalRequest.headers.Authorization = `Bearer ${access}`;
            this.processQueue(access, null);

            return this.client(originalRequest);
          } catch (refreshError) {
            this.processQueue(null, refreshError);
            this.logout();
            window.location.href = "/login";
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        return Promise.reject(error);
      },
    );
  }

  private processQueue(token: string | null, error: any) {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    this.failedQueue = [];
  }

  // Auth endpoints
  async signUp(data: SignUpRequest): Promise<AuthResponse> {
    const response = await this.client.post<AuthResponse>(
      "/api/users/signup/",
      data,
    );
    if (response.data.access) {
      localStorage.setItem("access_token", response.data.access);
      localStorage.setItem("refresh_token", response.data.refresh);
      // Store user data
      if (response.data.user) {
        localStorage.setItem("user_data", JSON.stringify(response.data.user));
      }
    }
    return response.data;
  }

  async login(data: LoginRequest): Promise<AuthResponse> {
    const response = await this.client.post<AuthResponse>(
      "/api/users/login/",
      data,
    );
    if (response.data.access) {
      localStorage.setItem("access_token", response.data.access);
      localStorage.setItem("refresh_token", response.data.refresh);
      // Store user data
      if (response.data.user) {
        localStorage.setItem("user_data", JSON.stringify(response.data.user));
      }
    }
    return response.data;
  }

  logout(): void {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user_data");
  }

  getUserData(): User | null {
    try {
      const userData = localStorage.getItem("user_data");
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  }

  setUserData(user: User): void {
    localStorage.setItem("user_data", JSON.stringify(user));
  }

  async refreshToken(): Promise<{ access: string; refresh?: string }> {
    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    const response = await axios.post<{ access: string; refresh?: string }>(
      `${API_BASE_URL}/api/users/token/refresh/`,
      { refresh: refreshToken },
    );

    if (response.data.access) {
      localStorage.setItem("access_token", response.data.access);
      if (response.data.refresh) {
        localStorage.setItem("refresh_token", response.data.refresh);
      }
    }

    return response.data;
  }

  async forgotPassword(email: string): Promise<{ detail: string }> {
    const normalized = (email || "").trim().toLowerCase();
    const response = await this.client.post<{ detail: string }>(
      "/api/users/forgot-password/",
      { email: normalized },
    );
    return response.data;
  }

  /** Matches ``django_rest_passwordreset`` URLs included under ``forgot-password/``. */
  async validateResetToken(token: string): Promise<{ detail: string }> {
    const response = await this.client.post<{ detail: string }>(
      "/api/users/forgot-password/validate_token/",
      { token },
    );
    return response.data;
  }

  async resetPassword(
    token: string,
    password: string,
    passwordConfirm: string,
  ): Promise<{ detail: string }> {
    void passwordConfirm;
    const response = await this.client.post<{ detail: string }>(
      "/api/users/forgot-password/confirm/",
      {
        token,
        password,
      },
    );
    return response.data;
  }

  async verifyEmail(otp: string): Promise<{ detail: string }> {
    const response = await this.client.post<{ detail: string }>(
      "/api/users/verify-email/",
      { otp },
    );
    return response.data;
  }

  async getProfile(): Promise<UserCreditsResponse> {
    const response = await this.client.get<UserCreditsResponse>(
      "/api/users/profile/",
    );
    return response.data;
  }

  async updateProfile(data: Partial<User>): Promise<User> {
    const response = await this.client.put<User>(
      "/api/users/update-profile/",
      data,
    );
    // Update stored user data
    this.setUserData(response.data);
    return response.data;
  }

  async changePassword(data: {
    current_password: string;
    new_password: string;
    password_confirm: string;
  }): Promise<{ detail: string }> {
    const response = await this.client.post<{ detail: string }>(
      "/api/users/change-password/",
      data,
    );
    return response.data;
  }

  // Game Modes endpoints
  async getGameModes(): Promise<GameMode[]> {
    const response = await this.client.get<any>("/api/game-modes/game-modes/");
    return normalizeArrayResponse<GameMode>(response.data);
  }

  async getCategories(): Promise<Category[]> {
    const response = await this.client.get<any>("/api/game-modes/categories/");
    return normalizeArrayResponse<Category>(response.data);
  }

  async getPaymentMethods(): Promise<PaymentMethod[]> {
    const response = await this.client.get<any>(
      "/api/game-modes/payment-methods/",
    );
    return normalizeArrayResponse<PaymentMethod>(response.data);
  }

  // Game Sessions endpoints
  async getMyGameSessions(): Promise<GameSession[]> {
    const response = await this.client.get<any>(
      "/api/game-sessions/my-sessions/",
    );
    return normalizeArrayResponse<GameSession>(response.data);
  }

  async createGameSession(
    data: CreateGameSessionRequest,
  ): Promise<GameSession> {
    const response = await this.client.post<GameSession>(
      "/api/game-sessions/create/",
      data,
    );
    return response.data;
  }

  async getGameSession(id: number): Promise<GameSession> {
    const response = await this.client.get<GameSession>(
      `/api/game-sessions/${id}/`,
    );
    return response.data;
  }

  async getImposterGameContent(contentId: number): Promise<any[]> {
    const response = await this.client.get<any>(
      `/api/game-sessions/imposter-content/${contentId}/`,
    );
    return normalizeArrayResponse<any>(response.data);
  }

  // Payments endpoints
  async getPurchaseOptions(): Promise<PurchaseOption[]> {
    const response = await this.client.get<any>(
      "/api/payments/purchase-options/",
    );
    return normalizeArrayResponse<PurchaseOption>(response.data);
  }

  async purchase(data: {
    purchase_option: number;
    discount_code?: number;
    payment_method?: number;
    token?: string;
    is_app?: boolean;
  }): Promise<{ payment_url?: string; detail?: string; invoice?: any }> {
    const response = await this.client.post("/api/payments/purchase/", data);
    return response.data;
  }

  async applyCoupon(
    code: string,
  ): Promise<{ detail: string; credits: number }> {
    const response = await this.client.post("/api/payments/apply-coupon/", {
      code,
    });
    return response.data;
  }

  async applyDiscountCode(
    code: string,
  ): Promise<{ discount_value: string; discount_code_id: number }> {
    const response = await this.client.post(
      "/api/payments/apply-discount-code/",
      { code },
    );
    return response.data;
  }

  async getPaymentResult(
    paymentId: string,
    status: string,
  ): Promise<{ detail: string; invoice?: any }> {
    const response = await this.client.get("/api/payments/payment-result/", {
      params: { paymentId, status },
    });
    return response.data;
  }

  // Public endpoint - Get content by identifier (for Imposter mode)
  async getContentByIdentifier(
    identifier: string,
  ): Promise<GameSessionModeCategoryContent> {
    const response = await this.client.get<GameSessionModeCategoryContent>(
      `/api/game-sessions/content/${identifier}/`,
    );
    return response.data;
  }

  async submitContactForm(data: {
    full_name?: string;
    email?: string;
    phone_number?: string;
    message: string;
  }): Promise<{ detail: string; contact: any }> {
    const response = await this.client.post("/api/forms/contact-us/", data);
    return response.data;
  }

  async getMyContactSubmissions(): Promise<any[]> {
    const response = await this.client.get("/api/forms/my-submissions/");
    return response.data;
  }

  // --- Per-mode APIs (`FRONTEND_MODES_BY_APP.md`) — path segment uses kebab-case in URLs.

  async getModesRegistry(): Promise<ModeRegistryEntry[]> {
    const response = await this.client.get<any>("/api/modes/registry/");
    return normalizeArrayResponse<ModeRegistryEntry>(response.data);
  }

  async getModeCategories(modePathSegment: string): Promise<ModeCategoryRow[]> {
    const response = await this.client.get<any>(
      `/api/modes/${modePathSegment}/categories/`,
    );
    return normalizeArrayResponse<ModeCategoryRow>(response.data);
  }

  async getModeCategoryContents(
    modePathSegment: string,
    categoryId: number,
  ): Promise<ModeWordContentRow[]> {
    const response = await this.client.get<any>(
      `/api/modes/${modePathSegment}/categories/${categoryId}/contents/`,
    );
    return normalizeArrayResponse<ModeWordContentRow>(response.data);
  }

  async getGuessCategoryContents(
    modePathSegment: string,
    categoryId: number,
  ): Promise<ModeGuessContentRow[]> {
    const response = await this.client.get<any>(
      `/api/modes/${modePathSegment}/categories/${categoryId}/contents/`,
    );
    return normalizeArrayResponse<ModeGuessContentRow>(response.data);
  }

  async getQuickQuestionContents(
    modePathSegment: string,
    categoryId: number,
  ): Promise<ModeQuickQuestionRow[]> {
    const response = await this.client.get<any>(
      `/api/modes/${modePathSegment}/categories/${categoryId}/contents/`,
    );
    return normalizeArrayResponse<ModeQuickQuestionRow>(response.data);
  }

  async getCategoryGameTopics(): Promise<ModeTopicRow[]> {
    const response = await this.client.get<any>("/api/modes/category/topics/");
    return normalizeArrayResponse<ModeTopicRow>(response.data);
  }

  async getDareCards(modePathSegment: string): Promise<ModeDareCardRow[]> {
    const response = await this.client.get<any>(
      `/api/modes/${modePathSegment}/cards/`,
    );
    return normalizeArrayResponse<ModeDareCardRow>(response.data);
  }

  async getAnswerBluffCategoryQuestions(
    categoryId: number,
  ): Promise<ModeQuickQuestionRow[]> {
    const response = await this.client.get<any>(
      `/api/modes/answer-bluff/categories/${categoryId}/questions/`,
    );
    return normalizeArrayResponse<ModeQuickQuestionRow>(response.data);
  }

  async postImposterStart(category_id: number): Promise<any> {
    const response = await this.client.post("/api/modes/imposter/start/", {
      category_id,
    });
    return response.data;
  }

  async postActingStart(category_ids: [number, number]): Promise<any> {
    const response = await this.client.post("/api/modes/acting/start/", {
      category_ids,
    });
    return response.data;
  }

  async postWhoAmIStart(category_id: number): Promise<any> {
    const response = await this.client.post("/api/modes/whoami/start/", {
      category_id,
    });
    return response.data;
  }

  async postGuessStart(category_ids: [number, number]): Promise<any> {
    const response = await this.client.post("/api/modes/guess/start/", {
      category_ids,
    });
    return response.data;
  }

  async postQuickQuestionsStart(category_ids: [number, number]): Promise<any> {
    const response = await this.client.post(
      "/api/modes/quick-questions/start/",
      { category_ids },
    );
    return response.data;
  }

  async postCategoryGameStart(): Promise<any> {
    const response = await this.client.post("/api/modes/category/start/", {});
    return response.data;
  }

  async postDareStart(modePathSegment: string = "dare"): Promise<any> {
    const response = await this.client.post(
      `/api/modes/${modePathSegment}/start/`,
      {},
    );
    return response.data;
  }

  async postAnswerBluffRoomStart(
    category_id: number,
  ): Promise<AnswerBluffRoomStartResponse> {
    const response = await this.client.post<AnswerBluffRoomStartResponse>(
      "/api/modes/answer-bluff/rooms/start/",
      { category_id },
    );
    return response.data;
  }

  async postAnswerBluffRoomControl(
    roomCode: string,
    action: string,
  ): Promise<AnswerBluffRoomControlResponse> {
    const code = encodeURIComponent(roomCode);
    const response = await this.client.post<AnswerBluffRoomControlResponse>(
      `/api/modes/answer-bluff/rooms/${code}/control/`,
      { action },
    );
    return response.data;
  }

  async postImposterOnlineRoomCreate(
    category_id: number,
    visibility: "private" | "public",
  ): Promise<ImposterOnlineRoomCreateResponse> {
    const response = await this.client.post<ImposterOnlineRoomCreateResponse>(
      "/api/modes/imposter/online/rooms/",
      { category_id, visibility },
    );
    return response.data;
  }

  async getImposterOnlinePublicLobbies(): Promise<ImposterOnlinePublicLobbiesResponse> {
    const response = await this.client.get<ImposterOnlinePublicLobbiesResponse>(
      "/api/modes/imposter/online/rooms/public/",
    );
    return response.data;
  }

  async postImposterOnlinePublicJoin(roomCode: string): Promise<ImposterOnlinePublicJoinResponse> {
    const code = encodeURIComponent(roomCode);
    const response = await this.client.post<ImposterOnlinePublicJoinResponse>(
      `/api/modes/imposter/online/rooms/${code}/join/`,
      {},
    );
    return response.data;
  }

  async postImposterOnlineRoomControl(
    roomCode: string,
    action: string,
    body?: Record<string, unknown>,
  ): Promise<AnswerBluffRoomControlResponse> {
    const code = encodeURIComponent(roomCode);
    const response = await this.client.post<AnswerBluffRoomControlResponse>(
      `/api/modes/imposter/online/rooms/${code}/control/`,
      { action, ...(body || {}) },
    );
    return response.data;
  }

  /** Personal role/word fallback when WebSocket hints lag (crew secret word). */
  async postImposterOnlineRoomHints(
    roomCode: string,
    body: { player_id: string; display_name?: string },
  ): Promise<{ me?: Record<string, unknown> }> {
    const code = encodeURIComponent(roomCode);
    const response = await this.client.post<{ me?: Record<string, unknown> }>(
      `/api/modes/imposter/online/rooms/${code}/hints/`,
      body,
    );
    return response.data;
  }
}

export const apiClient = new ApiClient();
